/**
 * Communications Dashboard Component
 * Coláiste Lurgan Management System - Email campaign management and analytics
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUserPermissions } from '../../hooks/useAuth';
import { canUseStorage } from '../../hooks/useCookieConsent';
import { useToast } from '../../hooks/use-toast';
import api from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import { Tooltip } from '../ui/tooltip';
import { Progress } from '../ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import EmailPreviewModal from '../communications/EmailPreviewModal';
import {
  Mail,
  Plus,
  Send,
  Eye,
  CheckCircle,
  XCircle,
  Search,
  Edit,
  Trash2,
  FileText,
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronRight,
  MailCheck,
  AlertTriangle,
  RefreshCw,
  Archive,
  StickyNote
} from 'lucide-react';

export function Communications() {
  const { userType, hasPermission } = useUserPermissions();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // State
  const [emails, setEmails] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('all'); // 'all', 'sent', 'drafts'
  const [hideAcknowledged, setHideAcknowledged] = useState(true); // Hide acknowledged errors by default
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Action states
  const [loadingStates, setLoadingStates] = useState({});
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [previewEmail, setPreviewEmail] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Task progress tracking (keyed by campaign UUID)
  const [taskProgress, setTaskProgress] = useState(new Map());
  const pollingIntervalRef = useRef(null);  // Use ref instead of state to avoid stale closures
  const sendBaselineRef = useRef({});  // Baseline counts before send, keyed by campaignUuid
  const autoRefreshIntervalRef = useRef(null);  // Auto-refresh every 30s
  const pollInFlightRef = useRef(false);  // Guard against overlapping poll requests

  // Lazy-loaded recipient lists (keyed by campaign UUID, loaded on row expand)
  const [campaignRecipients, setCampaignRecipients] = useState({});

  // Permission checks
  const isAdmin = userType === 'ADMIN';
  const canViewCommunications = isAdmin || hasPermission('view_communications');
  const canCreateCampaigns = isAdmin || hasPermission('add_communications');

  // Load campaign summaries from API
  const loadEmailsAndDrafts = async (showLoadingSpinner = true) => {
    if (showLoadingSpinner) {
      setLoading(true);
    }
    setError(null);

    try {
      // Get campaign summaries (grouped by campaign with success/failure counts)
      const campaignResponse = await api.emails.getCampaignSummary({ include_archived: true });
      const campaigns = campaignResponse || [];

      setEmails(campaigns);
    } catch (err) {
      // Error loading communications - handled by error state

      // Check if this might just be an empty database (common in development)
      const isLikelyEmptyDatabase = err?.response?.status === 500 ||
                                    err?.response?.status === 404 ||
                                    err?.message?.includes('No communications found');

      if (isLikelyEmptyDatabase) {
        // Treat as successful but empty response
        setEmails([]);
        setError(null); // Don't show error, show empty state instead
      } else {
        // For genuine errors (network, auth, etc.), show error
        setError(err.message || 'Failed to load communications');
        setEmails([]);
      }
    } finally {
      if (showLoadingSpinner) {
        setLoading(false);
      }
    }
  };

  // Poll actual email sending progress for a campaign
  const pollSendingProgress = async (campaignUuid, campaignName, totalRecipients) => {
    // Skip if a previous poll is still in-flight to prevent request pile-up
    if (pollInFlightRef.current) return;
    pollInFlightRef.current = true;
    try {
      // Get campaign summary to check actual sent emails
      const campaigns = await api.emails.getCampaignSummary();

      // Search by campaign_name instead of UUID (more reliable as UUID might change)
      const campaign = campaigns.find(c => c.campaign_name === campaignName) ||
                       campaigns.find(c => c.uuid === campaignUuid);

      if (!campaign) {
        console.error('Campaign not found:', campaignName, campaignUuid);
        console.error('Available campaigns:', campaigns.map(c => ({ name: c.campaign_name, uuid: c.uuid })));
        return;
      }

      // On first poll for this campaign, capture baseline counts so resends on
      // existing campaigns don't count historical sends/failures against this batch.
      if (!sendBaselineRef.current[campaignUuid]) {
        sendBaselineRef.current[campaignUuid] = {
          sent: campaign.successful_count || 0,
          failed: campaign.failed_count || 0,
        };
      }
      const baseline = sendBaselineRef.current[campaignUuid];

      const sentCount = Math.max(0, (campaign.successful_count || 0) - baseline.sent);
      const failedCount = Math.max(0, (campaign.failed_count || 0) - baseline.failed);
      const pendingCount = campaign.pending_count || 0;
      const totalProcessed = sentCount + failedCount;
      const percent = totalRecipients > 0 ? Math.round((totalProcessed / totalRecipients) * 100) : 0;


      // Update progress state with actual sending progress
      setTaskProgress(prev => {
        const newMap = new Map(prev);
        newMap.set(campaignUuid, {
          state: 'SENDING',
          progress: sentCount,
          total: totalRecipients,
          percent: percent,
          queued: sentCount,
          pending: pendingCount,
          failed: failedCount,
          skipped: 0
        });
        return newMap;
      });

      // Campaign is complete when pending hits 0 and the campaign's total_recipients
      // has reached the expected count. Using campaign.total_recipients (absolute)
      // instead of the baseline-relative totalProcessed avoids a race where the
      // baseline is captured after some emails are already sent, causing the
      // relative count to never reach totalRecipients.
      if (pendingCount === 0 && (campaign.total_recipients || 0) >= totalRecipients) {

        // Stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        // Clear from sessionStorage and baseline ref
        sessionStorage.removeItem('activeEmailTask');
        delete sendBaselineRef.current[campaignUuid];

        // Add small delay to ensure backend has fully processed all status updates
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Refresh email list silently (no loading spinner for better UX)
        await loadEmailsAndDrafts(false);

        // Show completion toast
        if (failedCount === 0) {
          toast({
            title: t('email.campaignComplete', 'Campaign Complete'),
            description: `"${campaignName}" sent to ${sentCount}/${totalRecipients} recipients`,
            variant: 'success',
            duration: 4000,
          });
        } else if (sentCount > 0) {
          toast({
            title: t('email.campaignPartial', 'Campaign Partially Complete'),
            description: `"${campaignName}" sent to ${sentCount}/${totalRecipients} recipients (${failedCount} failed)`,
            variant: 'warning',
            duration: 5000,
          });
        } else {
          toast({
            title: t('email.campaignFailed', 'Campaign Failed'),
            description: `"${campaignName}" failed to send to all recipients`,
            variant: 'destructive',
            duration: 5000,
          });
        }

        // Clear progress state after delay
        setTimeout(() => {
          setTaskProgress(prev => {
            const newMap = new Map(prev);
            newMap.delete(campaignUuid);
            return newMap;
          });
        }, 2000);
      }
    } catch (error) {
      console.error('Error polling sending progress:', error);
    } finally {
      pollInFlightRef.current = false;
    }
  };

  // Poll task status for active campaign (queuing phase)
  const pollTaskStatus = async (taskId, campaignUuid, campaignName) => {
    try {
      const status = await api.emails.getTaskStatus(taskId);

      // Update progress state for queuing phase
      setTaskProgress(prev => {
        const newMap = new Map(prev);
        newMap.set(campaignUuid, {
          ...status,
          state: 'QUEUING'
        });
        return newMap;
      });

      // If task completed (queuing done), switch to tracking actual sends
      if (status.state === 'SUCCESS') {
        const totalRecipients = status.total || status.progress || 0;

        // Stop polling the task status
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        // Start polling actual sending progress (don't call loadEmailsAndDrafts here - race condition!)
        const interval = setInterval(() => {
          pollSendingProgress(campaignUuid, campaignName, totalRecipients);
        }, 2000);

        pollingIntervalRef.current = interval;

        // Poll immediately once (this will also refresh the emails list)
        pollSendingProgress(campaignUuid, campaignName, totalRecipients);

      } else if (status.state === 'FAILURE') {
        // Queuing failed
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        sessionStorage.removeItem('activeEmailTask');
        await loadEmailsAndDrafts();

        toast({
          title: t('email.campaignFailed', 'Campaign Failed'),
          description: status.error || t('email.campaignFailedDescription', 'Failed to queue emails'),
          variant: 'destructive',
          duration: 5000,
        });

        // Clear progress state
        setTaskProgress(prev => {
          const newMap = new Map(prev);
          newMap.delete(campaignUuid);
          return newMap;
        });
      }
    } catch (error) {
      console.error('Error polling task status:', error);

      // Stop polling on error
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      // Clear progress for this campaign on error
      setTaskProgress(prev => {
        const newMap = new Map(prev);
        newMap.delete(campaignUuid);
        return newMap;
      });
    }
  };

  // Check for active task on mount and start polling
  useEffect(() => {
    const activeTaskData = sessionStorage.getItem('activeEmailTask');
    if (activeTaskData) {
      try {
        const taskData = JSON.parse(activeTaskData);

        // Start polling this task
        const interval = setInterval(() => {
          pollTaskStatus(taskData.task_id, taskData.campaign_uuid, taskData.campaign_name);
        }, 2000); // Poll every 2 seconds

        pollingIntervalRef.current = interval;  // Store in ref instead of state

        // Poll immediately once
        pollTaskStatus(taskData.task_id, taskData.campaign_uuid, taskData.campaign_name);

        // Cleanup on unmount
        return () => {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        };
      } catch (error) {
        console.error('Error parsing active task data:', error);
        sessionStorage.removeItem('activeEmailTask');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load data on component mount and when navigating to this page
  useEffect(() => {
    if (canViewCommunications) {
      loadEmailsAndDrafts();
    }
  }, [canViewCommunications, location.key]); // location.key changes on navigation

  // Auto-refresh every 30 seconds while on Communications page
  useEffect(() => {
    if (!canViewCommunications) return;

    // Start auto-refresh interval (only if not actively polling a campaign)
    const startAutoRefresh = () => {
      autoRefreshIntervalRef.current = setInterval(() => {
        // Only auto-refresh if we're not actively polling a campaign
        if (!pollingIntervalRef.current) {
          loadEmailsAndDrafts(false); // Silent refresh (no loading spinner)
        }
      }, 30000); // 30 seconds
    };

    startAutoRefresh();

    // Cleanup on unmount
    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
        autoRefreshIntervalRef.current = null;
      }
    };
  }, [canViewCommunications]);

  // Filter emails based on view mode, search, and status
  useEffect(() => {
    let allItems = [];

    // Filter based on view mode
    if (viewMode === 'all') {
      allItems = emails.map(email => ({
        ...email,
        type: email.status === 'Draft' ? 'draft' : 'sent'
      }));
    } else if (viewMode === 'sent') {
      allItems = emails
        .filter(email => email.status !== 'Draft')
        .map(email => ({ ...email, type: 'sent' }));
    } else if (viewMode === 'drafts') {
      allItems = emails
        .filter(email => email.status === 'Draft')
        .map(email => ({ ...email, type: 'draft' }));
    }

    // Filter archived items
    if (!showArchived) {
      allItems = allItems.filter(item => !item.is_archived);
    }

    // Apply search filter
    if (searchTerm) {
      allItems = allItems.filter(item =>
        (item.email_type && item.email_type.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.subject && item.subject.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.sent_by_name && item.sent_by_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      allItems = allItems.filter(item => item.status === statusFilter);
    }

    // Sort by creation/sent date (most recent first)
    allItems.sort((a, b) => {
      const dateA = new Date(a.sent_at || a.created_at || a.updated_at);
      const dateB = new Date(b.sent_at || b.created_at || b.updated_at);
      return dateB - dateA;
    });

    setFilteredItems(allItems);
  }, [emails, viewMode, searchTerm, statusFilter, hideAcknowledged, showArchived]);

  // Campaign status badge styling (new statuses: Draft, Sent, Partial, Failed, Sending)
  const getStatusBadge = (campaign) => {
    // Check if this campaign is currently being processed
    const progress = taskProgress.get(campaign.uuid);
    const isProcessing = progress && (progress.state === 'QUEUING' || progress.state === 'SENDING');

    if (isProcessing) {
      const label = progress.state === 'QUEUING' ? 'Queuing' : 'Sending';
      return (
        <Badge variant="secondary" className="flex items-center gap-1 bg-blue-50 text-blue-700 border-blue-200">
          <Loader2 className="h-3 w-3 animate-spin" />
          {label}
        </Badge>
      );
    }

    const status = campaign.status;
    const statusConfig = {
      'Draft': {
        variant: 'secondary',
        icon: FileText,
        color: 'text-gray-600',
        text: 'Draft'
      },
      'Sent': {
        variant: 'default',
        icon: CheckCircle,
        color: 'text-green-600',
        text: 'Sent ✓'
      },
      'Partial': {
        variant: 'warning',
        icon: AlertTriangle,
        color: 'text-orange-600',
        text: 'Partial ⚠️'
      },
      'Failed': {
        variant: 'destructive',
        icon: XCircle,
        color: 'text-red-600',
        text: 'Failed ❌'
      }
    };

    const config = statusConfig[status] || statusConfig['Draft'];
    const StatusIcon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <StatusIcon className="h-3 w-3" />
        {config.text}
      </Badge>
    );
  };

  // Toggle row expansion
  const toggleRow = (campaignId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(campaignId)) {
        newSet.delete(campaignId);
        // Clear cached recipients when collapsing so the next expand fetches fresh data
        setCampaignRecipients(prev => {
          const next = { ...prev };
          delete next[campaignId];
          return next;
        });
      } else {
        newSet.add(campaignId);
        // Always fetch fresh recipients on expand
        api.emails.getCampaignRecipients(campaignId).then(data => {
          setCampaignRecipients(prev => ({ ...prev, [campaignId]: data }));
        }).catch(err => console.error('Failed to load campaign recipients:', err));
      }
      return newSet;
    });
  };

  // Helper to get recipients for a campaign (either cached or empty)
  const getRecipients = (campaignId) => campaignRecipients[campaignId] || { successful_recipients: [], failed_recipients: [] };

  // Fetch recipients for a campaign, loading them if not yet cached
  const fetchRecipients = async (campaignId) => {
    if (campaignRecipients[campaignId]) return campaignRecipients[campaignId];
    const data = await api.emails.getCampaignRecipients(campaignId);
    setCampaignRecipients(prev => ({ ...prev, [campaignId]: data }));
    return data;
  };

  // Handle draft editing
  const handleEditDraft = (draftUuid) => {
    navigate(`/communications/edit/${draftUuid}`);
  };

  // Handle email preview
  const handleViewEmail = (email) => {
    setPreviewEmail(email);
  };

  // Handle send again - go directly to recipients page with existing draft
  const handleSendAgain = (email) => {
    navigate(`/communications/edit/${email.uuid}?page=2`);
  };

  // Handle edit campaign - go to content page to edit before sending
  const handleEditCampaign = (email) => {
    navigate(`/communications/edit/${email.uuid}`);
  };

  // Handle archive/unarchive campaign
  const handleArchive = async (item) => {
    setLoadingStates(prev => ({ ...prev, [`archive_${item.uuid}`]: true }));
    try {
      const result = await api.emails.archiveCampaign(item.uuid);
      if (result.is_archived) {
        setEmails(prev => prev.filter(e => String(e.uuid) !== String(item.uuid)));
      } else {
        setEmails(prev => prev.map(e =>
          String(e.uuid) === String(item.uuid) ? { ...e, is_archived: false } : e
        ));
      }
      toast({
        title: result.is_archived ? t('communications.archived', 'Archived') : t('communications.unarchived', 'Unarchived'),
        description: result.is_archived
          ? t('communications.archivedDescription', 'Campaign archived successfully.')
          : t('communications.unarchivedDescription', 'Campaign restored successfully.'),
        variant: 'success',
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('communications.archiveError', 'Failed to archive campaign.'),
        variant: 'destructive',
      });
    } finally {
      setLoadingStates(prev => ({ ...prev, [`archive_${item.uuid}`]: false }));
    }
  };

  // Handle draft deletion
  const handleDeleteDraft = async (draftId) => {
    setLoadingStates(prev => ({ ...prev, [draftId]: true }));
    try {
      await api.emails.deleteDraft(draftId);

      // Remove from local state
      setEmails(prevEmails => prevEmails.filter(email => email.uuid !== draftId));

      // Show success toast
      toast({
        title: t('email.draftDeleted'),
        description: t('email.draftDeletedDescription'),
        variant: "success",
        duration: 3000,
      });
    } catch (error) {
      // Error deleting draft - handled by toast notification

      // Show error toast
      toast({
        title: t('email.draftDeleteFailed'),
        description: error.response?.data?.message || t('email.draftDeleteFailedDescription'),
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setLoadingStates(prev => ({ ...prev, [draftId]: false }));
    }
  };

  // Handle delete confirmation
  const confirmDelete = async () => {
    if (deleteConfirmId) {
      await handleDeleteDraft(deleteConfirmId);
      setDeleteConfirmId(null);
    }
  };

  // Handle retry failed recipients
  const handleRetryFailed = async (campaign) => {
    if (campaign.failed_count === 0) {
      return;
    }

    setLoadingStates(prev => ({ ...prev, [campaign.uuid]: true }));

    try {
      // Fetch recipients if not already loaded
      const recipients = await fetchRecipients(campaign.uuid);
      const failedRecipientUuids = recipients.failed_recipients.map(r => r.uuid);

      // Send campaign to failed recipients only
      const response = await api.emails.sendDraft(campaign.uuid, {
        recipients: failedRecipientUuids,
        delete_draft_after_send: false // Keep the draft/template
      });

      // Check if this is a background task response (async sending)
      const isBackgroundTask = response.data?.task_id && response.data?.campaign_sent;

      if (isBackgroundTask) {
        // Background task response - start polling progress
        const taskData = {
          task_id: response.data.task_id,
          campaign_uuid: response.data.campaign_uuid || campaign.uuid,
          campaign_name: campaign.campaign_name || campaign.subject,
          total_recipients: response.data.total_recipients || failedRecipientUuids.length
        };

        // Store in sessionStorage for recovery if page refreshes (requires consent)
        if (canUseStorage()) {
          sessionStorage.setItem('activeEmailTask', JSON.stringify(taskData));
        }

        // Start polling task status
        const interval = setInterval(() => {
          pollTaskStatus(taskData.task_id, taskData.campaign_uuid, taskData.campaign_name);
        }, 2000);
        pollingIntervalRef.current = interval;

        // Poll immediately once
        pollTaskStatus(taskData.task_id, taskData.campaign_uuid, taskData.campaign_name);

        // Show info toast
        toast({
          title: t('communications.retryQueued', 'Retry Queued'),
          description: t('communications.retryQueuedDescription', `Retrying ${failedRecipientUuids.length} failed recipient(s) in the background`),
          variant: "default",
          duration: 3000,
        });
      } else {
        // Synchronous response (old behavior for backward compatibility)
        await loadEmailsAndDrafts();

        const hasWarning = response.data.warning;
        toast({
          title: hasWarning
            ? t('communications.retryPartialSuccess', 'Retry Partially Successful')
            : t('communications.retrySuccess', 'Retry Successful'),
          description: hasWarning
            ? `${response.data.emails_sent} email(s) sent. ${response.data.warning}`
            : t('communications.retrySuccessDescription', `Retrying ${campaign.failed_count} failed recipients`),
          variant: hasWarning ? "warning" : "success",
          duration: hasWarning ? 5000 : 3000,
        });
      }
    } catch (error) {
      // Show error toast
      toast({
        title: t('communications.retryFailed', 'Retry Failed'),
        description: error.response?.data?.message || t('communications.retryFailedDescription', 'Failed to retry sending'),
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setLoadingStates(prev => ({ ...prev, [campaign.uuid]: false }));
    }
  };

  // Handle acknowledge failed emails (mark as reviewed)
  const handleAcknowledgeFailed = async (campaign) => {
    if (campaign.unacknowledged_failed_count === 0) {
      return;
    }

    setLoadingStates(prev => ({ ...prev, [`ack_${campaign.uuid}`]: true }));

    try {
      // Fetch recipients if not already loaded
      const recipients = await fetchRecipients(campaign.uuid);
      // Only acknowledge emails that haven't been reviewed yet
      const failedEmailUuids = recipients.failed_recipients
        .filter(r => !r.acknowledged)
        .map(r => r.email_uuid)
        .filter(Boolean);

      if (failedEmailUuids.length === 0) {
        toast({
          title: t('communications.noEmailsToAcknowledge', 'No Emails to Acknowledge'),
          description: t('communications.noEmailsToAcknowledgeDescription', 'No failed emails found to acknowledge'),
          variant: "warning",
          duration: 3000,
        });
        return;
      }

      // Bulk acknowledge failed emails
      await api.emails.bulkAcknowledge(failedEmailUuids);

      // Reload emails to get updated counts
      await loadEmailsAndDrafts();

      // Show success toast
      toast({
        title: t('communications.acknowledgeSuccess', 'Emails Acknowledged'),
        description: t('communications.acknowledgeSuccessDescription', `${failedEmailUuids.length} failed email(s) marked as reviewed`),
        variant: "success",
        duration: 3000,
      });
    } catch (error) {
      // Show error toast
      toast({
        title: t('communications.acknowledgeFailed', 'Acknowledge Failed'),
        description: error.response?.data?.message || t('communications.acknowledgeFailedDescription', 'Failed to acknowledge emails'),
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setLoadingStates(prev => ({ ...prev, [`ack_${campaign.uuid}`]: false }));
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!canViewCommunications) {
    return (
      <div className="space-y-6">
        <Alert>
          <XCircle className="h-4 w-4" />
          <AlertDescription>
            {t('communications.noPermission')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Compact Header */}
      <div className="flex justify-between items-center pb-4 border-b">
        <div className="flex items-center space-x-4">
          <div>
            <h1 className="text-xl font-semibold">{t('communications.title')}</h1>
          </div>

          {/* Inline Statistics */}
          <div className="flex items-center space-x-2 text-muted-foreground text-sm border-l pl-4">
            <Mail className="h-4 w-4" />
            <span>
              {loading ? (
                <div className="h-4 w-12 bg-muted animate-pulse rounded inline-block" />
              ) : (
                <>
                  <span className="font-medium text-foreground">{filteredItems.length}</span>
                  {' '}
                  {filteredItems.length === emails.length ? t('communications.total') : t('communications.filtered')}
                </>
              )}
            </span>
          </div>
        </div>

        <div className="flex gap-2">
          {/* Manual Refresh Button */}
          <Button
            variant="outline"
            onClick={loadEmailsAndDrafts}
            disabled={loading}
            title="Refresh campaign list"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>

          {canCreateCampaigns && (
            <Button onClick={() => navigate('/communications/create')} disabled={loading}>
              {loading ? (
                <div className="mr-2 h-4 w-4 bg-muted animate-pulse rounded" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              {t('communications.newEmail')}
            </Button>
          )}
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between py-4">
        <div className="flex gap-4 flex-1 items-center flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('communications.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-input bg-background rounded-md text-sm min-w-[140px]"
          >
            <option value="all">{t('communications.allStatuses')}</option>
            <option value="Draft">{t('communications.drafts')}</option>
            <option value="Sent">{t('communications.sent')}</option>
            <option value="Partial">{t('communications.partial', 'Partial')}</option>
            <option value="Failed">{t('communications.failed')}</option>
          </select>

          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            className="px-3 py-2 border border-input bg-background rounded-md text-sm min-w-[120px]"
          >
            <option value="all">{t('communications.allEmails')}</option>
            <option value="sent">{t('communications.sentOnly')}</option>
            <option value="drafts">{t('communications.draftsOnly')}</option>
          </select>

          {/* Hide Acknowledged Errors Checkbox */}
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer whitespace-nowrap">
            <input
              type="checkbox"
              checked={hideAcknowledged}
              onChange={(e) => setHideAcknowledged(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300"
            />
            <span>{t('communications.hideAcknowledged', 'Hide reviewed errors')}</span>
          </label>
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer whitespace-nowrap">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300"
            />
            <span>{t('communications.showArchived', 'Show archived')}</span>
          </label>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error}
            <Button
              variant="link"
              className="p-0 h-auto ml-2"
              onClick={loadEmailsAndDrafts}
            >
              {t('common.tryAgain')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Communications Table */}
      <Card className="flex-1">
        <CardContent className="p-0">
          <div className="border rounded-lg">
            <table className="w-full">
              <thead className="border-b bg-gray-50/50 dark:bg-muted/30">
                <tr>
                  <th className="w-12"></th>
                  <th className="text-left p-4 font-medium text-sm">{t('communications.subject')}</th>
                  <th className="text-left p-4 font-medium text-sm">{t('communications.type')}</th>
                  <th className="text-left p-4 font-medium text-sm">{t('communications.recipients', 'Recipients')}</th>
                  <th className="text-left p-4 font-medium text-sm">{t('communications.status')}</th>
                  <th className="text-left p-4 font-medium text-sm">{t('communications.created')}</th>
                  <th className="text-right p-4 font-medium text-sm">{t('communications.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  // Loading skeleton rows
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b">
                      <td className="p-4">
                        <div className="h-4 bg-muted animate-pulse rounded w-20" />
                      </td>
                      <td className="p-4">
                        <div className="h-4 bg-muted animate-pulse rounded w-32" />
                      </td>
                      <td className="p-4">
                        <div className="h-6 bg-muted animate-pulse rounded w-16" />
                      </td>
                      <td className="p-4">
                        <div className="h-4 bg-muted animate-pulse rounded w-24" />
                      </td>
                      <td className="p-4">
                        <div className="h-4 bg-muted animate-pulse rounded w-24" />
                      </td>
                      <td className="p-4">
                        <div className="h-8 bg-muted animate-pulse rounded w-16 ml-auto" />
                      </td>
                    </tr>
                  ))
                ) : filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-muted-foreground">
                      <Mail className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                      <p className="text-lg font-medium mb-2">{t('communications.noEmails')}</p>
                      <p className="text-sm">{t('communications.createFirstEmail')}</p>
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item) => {
                    const isExpanded = expandedRows.has(item.uuid);
                    const isDraft = item.status === 'Draft';
                    const hasRecipients = item.total_recipients > 0;

                    return (
                      <React.Fragment key={item.uuid}>
                        <tr className="border-b hover:bg-gray-50/50 dark:hover:bg-muted/30">
                          {/* Expand/Collapse Button */}
                          <td className="p-4 w-12">
                            {hasRecipients && (
                              <button
                                onClick={() => toggleRow(item.uuid)}
                                className="p-1 hover:bg-gray-200 dark:hover:bg-muted rounded"
                                aria-label={isExpanded ? 'Collapse' : 'Expand'}
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </button>
                            )}
                          </td>

                          {/* Subject */}
                          <td className="p-4">
                            <span className="text-sm font-medium">{item.subject}</span>
                          </td>

                          {/* Type */}
                          <td className="p-4">
                            <span className="text-sm text-muted-foreground">{item.email_type}</span>
                          </td>

                          {/* Recipients Count with Progress */}
                          <td className="p-4">
                            {(() => {
                              const progress = taskProgress.get(item.uuid);
                              const isProcessing = progress && (progress.state === 'QUEUING' || progress.state === 'SENDING');

                              if (isProcessing) {
                                // Show inline progress while queuing/sending
                                return (
                                  <div className="text-sm space-y-1.5">
                                    <div className="font-medium">{progress.total || 0} {t('communications.total', 'total')}</div>
                                    <div className="w-full">
                                      <Progress value={progress.percent || 0} className="h-2" />
                                    </div>
                                    <div className="flex gap-3 text-xs">
                                      {progress.progress > 0 && (
                                        <span className="text-green-600">✓ {progress.progress} {t('communications.queued', 'queued')}</span>
                                      )}
                                      {progress.skipped > 0 && (
                                        <span className="text-gray-600">⊘ {progress.skipped} {t('communications.skipped', 'skipped')}</span>
                                      )}
                                    </div>
                                  </div>
                                );
                              }

                              // Show normal recipient counts when not sending
                              if (hasRecipients) {
                                return (
                                  <div className="text-sm">
                                    <div className="font-medium">{item.total_recipients} {t('communications.total', 'total')}</div>
                                    {item.successful_count > 0 && (
                                      <div className="text-xs text-green-600">
                                        ✓ {item.successful_count} {t('communications.sent', 'sent')}
                                      </div>
                                    )}
                                    {item.failed_count > 0 && (
                                      <div className="text-xs text-red-600">
                                        ✗ {hideAcknowledged ? item.unacknowledged_failed_count : item.failed_count} {t('communications.failed', 'failed')}
                                        {item.total_failed_attempts > item.failed_count && (
                                          <span className="text-gray-500 dark:text-gray-400 ml-1">
                                            ({item.total_failed_attempts} {t('communications.attempts', 'attempts')})
                                          </span>
                                        )}
                                        {hideAcknowledged && item.acknowledged_failed_count > 0 && (
                                          <span className="text-gray-500 dark:text-gray-400 ml-1">
                                            (+{item.acknowledged_failed_count} {t('communications.reviewed', 'reviewed')})
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              }

                              return <span className="text-sm text-muted-foreground">—</span>;
                            })()}
                          </td>

                          {/* Status */}
                          <td className="p-4">
                            {getStatusBadge(item)}
                          </td>

                          {/* Created Date */}
                          <td className="p-4">
                            <span className="text-sm text-muted-foreground">
                              {formatDate(item.created_at)}
                            </span>
                          </td>

                          {/* Actions */}
                          <td className="p-4">
                            <div className="flex gap-2 justify-end">
                              {isDraft ? (
                            <>
                              <Tooltip content={t('communications.editDraft', 'Edit Template')} side="top">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEditDraft(item.uuid)}
                                  disabled={loadingStates[item.uuid]}
                                  className="text-blue-600 hover:text-blue-800 hover:border-blue-300"
                                  aria-label={t('communications.editDraft', 'Edit Template')}
                                >
                                  {loadingStates[item.uuid] ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Edit className="h-4 w-4" />
                                  )}
                                </Button>
                              </Tooltip>
                              <Tooltip content={t('communications.deleteDraft', 'Delete Template')} side="top">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setDeleteConfirmId(item.uuid)}
                                  disabled={loadingStates[item.uuid]}
                                  className="text-red-600 hover:text-red-800 hover:border-red-300"
                                  aria-label={t('communications.deleteDraft', 'Delete Template')}
                                >
                                  {loadingStates[item.uuid] ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4" />
                                  )}
                                </Button>
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              {item.unacknowledged_failed_count > 0 && (
                                <>
                                  <Tooltip content={t('communications.retryFailed', 'Retry Failed')} side="top">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleRetryFailed(item)}
                                      disabled={loadingStates[item.uuid]}
                                      className="text-orange-600 hover:text-orange-800 hover:border-orange-300"
                                      aria-label={t('communications.retryFailed', 'Retry Failed')}
                                    >
                                      {loadingStates[item.uuid] ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <RefreshCw className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </Tooltip>
                                  {item.unacknowledged_failed_count > 0 && (
                                    <Tooltip content={t('communications.acknowledgeFailed', 'Mark as Reviewed')} side="top">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleAcknowledgeFailed(item)}
                                        disabled={loadingStates[`ack_${item.uuid}`]}
                                        className="text-gray-600 hover:text-gray-800 hover:border-gray-300"
                                        aria-label={t('communications.acknowledgeFailed', 'Mark as Reviewed')}
                                      >
                                        {loadingStates[`ack_${item.uuid}`] ? (
                                          <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                          <CheckCircle className="h-4 w-4" />
                                        )}
                                      </Button>
                                    </Tooltip>
                                  )}
                                </>
                              )}
                              <Tooltip content={t('communications.editCampaign', 'Edit & Resend')} side="top">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEditCampaign(item)}
                                  className="text-blue-600 hover:text-blue-800 hover:border-blue-300"
                                  aria-label={t('communications.editCampaign', 'Edit & Resend')}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </Tooltip>
                              <Tooltip content={t('communications.previewEmail')} side="top">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewEmail(item)}
                                  className="text-green-600 hover:text-green-800 hover:border-green-300"
                                  aria-label={t('communications.previewEmail')}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </Tooltip>
                              <Tooltip content={t('communications.sendAgain')} side="top">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleSendAgain(item)}
                                  className="text-blue-600 hover:text-blue-800 hover:border-blue-300"
                                  aria-label={t('communications.sendAgain')}
                                >
                                  <Send className="h-4 w-4" />
                                </Button>
                              </Tooltip>
                              <Tooltip content={item.is_archived ? t('communications.unarchive', 'Unarchive') : t('communications.archive', 'Archive')} side="top">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleArchive(item)}
                                  disabled={loadingStates[`archive_${item.uuid}`]}
                                  className="text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                  aria-label={item.is_archived ? t('communications.unarchive', 'Unarchive') : t('communications.archive', 'Archive')}
                                >
                                  {loadingStates[`archive_${item.uuid}`] ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Archive className="h-4 w-4" />
                                  )}
                                </Button>
                              </Tooltip>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Expandable Row Content */}
                    {isExpanded && hasRecipients && (
                      <tr className="bg-gray-50 dark:bg-muted/20">
                        <td colSpan={7} className="p-0">
                          <div className="p-6 space-y-4">
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                              {t('communications.recipientDetails', 'Recipient Details')} — {item.subject}
                            </div>

                            <div className="grid grid-cols-2 gap-6">
                              {/* Successful Recipients */}
                              {item.successful_count > 0 && (
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2 font-medium text-green-700">
                                    <MailCheck className="h-4 w-4" />
                                    <span>{t('communications.successful', 'Successful')} ({item.successful_count})</span>
                                  </div>
                                  <div className="bg-white dark:bg-background border rounded-lg max-h-60 overflow-y-auto">
                                    <table className="w-full text-sm">
                                      <thead className="bg-gray-50 dark:bg-muted/50 border-b sticky top-0">
                                        <tr>
                                          <th className="text-left p-2 font-medium">{t('communications.student', 'Student')}</th>
                                          <th className="text-left p-2 font-medium">{t('communications.sentAt', 'Sent At')}</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {getRecipients(item.uuid).successful_recipients?.map((recipient) => (
                                          <tr key={recipient.uuid} className="border-b last:border-b-0 hover:bg-gray-50 dark:hover:bg-muted/30">
                                            <td className="p-2">
                                              <div className="flex items-center gap-1.5">
                                                <div className="font-medium">{recipient.name}</div>
                                                {recipient.notes && (
                                                  <div className="relative group shrink-0">
                                                    <StickyNote className="h-3.5 w-3.5 text-amber-500" />
                                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 max-w-48 whitespace-normal text-center">
                                                      {recipient.notes.length > 120 ? `${recipient.notes.slice(0, 120)}…` : recipient.notes}
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                              <div className="text-xs text-gray-500 dark:text-gray-400">{recipient.email}</div>
                                            </td>
                                            <td className="p-2 text-gray-600 dark:text-gray-400">
                                              {formatDate(recipient.sent_at)}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}

                              {/* Failed Recipients (filtered by acknowledged status) */}
                              {item.failed_count > 0 && (() => {
                                const displayedFailedRecipients = hideAcknowledged
                                  ? getRecipients(item.uuid).failed_recipients?.filter(r => !r.acknowledged)
                                  : getRecipients(item.uuid).failed_recipients;
                                const displayCount = hideAcknowledged ? item.unacknowledged_failed_count : item.failed_count;

                                return displayedFailedRecipients && displayedFailedRecipients.length > 0 && (
                                  <div className="space-y-2">
                                    <div className="flex items-center gap-2 font-medium text-red-700">
                                      <XCircle className="h-4 w-4" />
                                      <span>
                                        {t('communications.failed', 'Failed')} ({displayCount} {t('communications.students', 'students')})
                                        {item.total_failed_attempts > item.failed_count && (
                                          <span className="text-sm font-normal text-gray-600 dark:text-gray-400 ml-2">
                                            {item.total_failed_attempts} {t('communications.totalAttempts', 'total attempts')}
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                    <div className="bg-white dark:bg-background border rounded-lg max-h-60 overflow-y-auto">
                                      <table className="w-full text-sm">
                                        <thead className="bg-gray-50 dark:bg-muted/50 border-b sticky top-0">
                                          <tr>
                                            <th className="text-left p-2 font-medium">{t('communications.student', 'Student')}</th>
                                            <th className="text-left p-2 font-medium">{t('communications.reason', 'Reason')}</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {displayedFailedRecipients.map((recipient) => (
                                            <tr key={recipient.uuid} className="border-b last:border-b-0 hover:bg-gray-50 dark:hover:bg-muted/30">
                                              <td className="p-2">
                                                <div className="flex items-center gap-2">
                                                  <div>
                                                    <div className="flex items-center gap-1.5">
                                                      <div className="font-medium">{recipient.name}</div>
                                                      {recipient.notes && (
                                                        <div className="relative group shrink-0">
                                                          <StickyNote className="h-3.5 w-3.5 text-amber-500" />
                                                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 max-w-48 whitespace-normal text-center">
                                                            {recipient.notes.length > 120 ? `${recipient.notes.slice(0, 120)}…` : recipient.notes}
                                                          </div>
                                                        </div>
                                                      )}
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">{recipient.email}</div>
                                                  </div>
                                                  {recipient.acknowledged && (
                                                    <span className="text-xs text-gray-500 dark:text-gray-400 italic">
                                                      ({t('communications.reviewed', 'reviewed')})
                                                    </span>
                                                  )}
                                                </div>
                                              </td>
                                              <td className="p-2 text-red-600 text-xs">
                                                {recipient.error || 'Unknown error'}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmId !== null} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('communications.confirmDeleteTitle')}</DialogTitle>
            <DialogDescription>
              {t('communications.confirmDeleteDescription')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteConfirmId && loadingStates[deleteConfirmId]}
            >
              {deleteConfirmId && loadingStates[deleteConfirmId] ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Preview Modal */}
      <EmailPreviewModal
        email={previewEmail}
        isOpen={previewEmail !== null}
        onClose={() => setPreviewEmail(null)}
      />
    </div>
  );
}

export default Communications;